/**
 * AddressAutocomplete Component (AutocompleteService - Custom UI)
 *
 * Custom Google Places Autocomplete component with full styling control.
 * Uses AutocompleteService (programmatic API) instead of Legacy Autocomplete widget.
 *
 * Benefits:
 * - Full control over UI/UX and styling
 * - Not deprecated (modern API with long-term support)
 * - Same pricing as Legacy with session tokens (~$0.051/session)
 * - Keyboard navigation support (Arrow keys, Enter, Escape)
 * - Click outside to close
 * - Responsive design
 *
 * @module AddressAutocomplete
 */

import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useAutocompleteService } from '../hooks/useAutocompleteService';
import { GooglePlacesParser } from '../utils/googlePlacesParser';
import { DEBUG_MODE } from '../constants/googlePlaces';
import logger from '../utils/logger';
import './AddressAutocomplete.css';

/**
 * AddressAutocomplete Component
 *
 * @param {Object} props - Component props
 * @param {string} props.value - Current input value (controlled component)
 * @param {Function} props.onChange - Callback when input changes (e, value)
 * @param {Function} props.onPlaceSelected - Callback when place is selected ({ parsedAddress, streetAddress })
 * @param {Function} [props.onAddressAutofilled] - Callback after address is auto-filled (for side effects)
 * @param {string} [props.id='shippingAddress'] - Input element ID
 * @param {string} [props.name='address'] - Input element name
 * @param {string} [props.placeholder='Address'] - Input placeholder
 * @param {boolean} [props.required=false] - Whether input is required
 * @param {string} [props.apartmentFieldId='shippingApt'] - ID of apartment field to focus after selection
 * @param {boolean} [props.enableAnalytics=true] - Enable Google Tag Manager tracking
 * @param {boolean} [props.enabled=true] - Enable/disable autocomplete functionality
 * @param {number} [props.debounceMs=300] - Debounce delay in milliseconds
 *
 * @example
 * <AddressAutocomplete
 *   value={formData.address}
 *   onChange={(e, value) => setFormData({ ...formData, address: value })}
 *   onPlaceSelected={({ parsedAddress, streetAddress }) => {
 *     setFormData({
 *       ...formData,
 *       address: streetAddress,
 *       city: parsedAddress.city,
 *       state: parsedAddress.state,
 *       zip: parsedAddress.zip
 *     });
 *   }}
 * />
 */
export const AddressAutocomplete = ({
  value,
  onChange,
  onPlaceSelected,
  onAddressAutofilled,
  onFocus,
  id = 'shippingAddress',
  name = 'address',
  placeholder = 'Address',
  required = false,
  apartmentFieldId = 'shippingApt',
  enableAnalytics = true,
  enabled = true,
  debounceMs = 300,
  countryCode = 'US',
}) => {
  // Refs
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const isSelectingRef = useRef(false); // Flag to prevent search during selection

  // Local state
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  // Use AutocompleteService hook
  const { suggestions, loading, error, getPlaceDetails, getZipCodeFromLocation, clearSuggestions, updatePreviousInput } = useAutocompleteService({
    input: isSelectingRef.current ? '' : value, // Don't search while selecting
    debounceMs,
    enabled,
    countryCode: countryCode?.toLowerCase() || 'us',
  });

  /**
   * Track analytics event
   */
  const trackAnalytics = useCallback(() => {
    if (!enableAnalytics || typeof window === 'undefined' || !window.dataLayer) {
      return;
    }

    try {
      window.dataLayer.push({
        event: 'address_autocomplete_used',
        address_source: 'google_places_autocomplete_service',
      });

      if (DEBUG_MODE) {
        logger.debug('[AddressAutocomplete] ✅ Analytics event tracked');
      }
    } catch (err) {
      logger.warn('[AddressAutocomplete] ⚠️ Failed to track analytics:', err);
    }
  }, [enableAnalytics]);

  /**
   * Handle place selection
   */
  const handlePlaceSelect = useCallback(
    (prediction) => {
      if (DEBUG_MODE) {
        logger.debug('[AddressAutocomplete] 🗺️ Place selected:', prediction);
      }

      // Get full place details by place_id
      getPlaceDetails(prediction.place_id, (place, error) => {
        if (error) {
          console.error('[AddressAutocomplete] ❌ Error fetching place details:', error);
          return;
        }

        if (DEBUG_MODE) {
          logger.debug('[AddressAutocomplete] ✅ Got place details:', place);
        }

        // Parse address using utility
        let { parsedAddress, streetAddress } = GooglePlacesParser.parsePlace(place);

        if (DEBUG_MODE) {
          logger.debug('[AddressAutocomplete] 📍 Parsed address:', parsedAddress);
          logger.debug('[AddressAutocomplete] 🏠 Street address:', streetAddress);
        }

        // Warn if generic location selected (no street)
        if (GooglePlacesParser.isGenericLocation(parsedAddress)) {
          logger.warn(
            '[AddressAutocomplete] ⚠️ Generic location selected (city/region). User must enter street address manually.'
          );
        }

        // 🔄 If ZIP is missing, try reverse geocoding with geometry location
        if (!parsedAddress.zip && place.geometry && place.geometry.location) {
          if (DEBUG_MODE) {
            logger.debug('[AddressAutocomplete] 🔄 ZIP missing, attempting reverse geocoding...');
          }

          getZipCodeFromLocation(place.geometry.location, (zipCode, error) => {
            if (zipCode) {
              // Update parsedAddress with found ZIP
              parsedAddress = { ...parsedAddress, zip: zipCode };

              if (DEBUG_MODE) {
                logger.debug('[AddressAutocomplete] ✅ Updated ZIP via reverse geocoding:', zipCode);
              }
            } else if (DEBUG_MODE) {
              logger.warn('[AddressAutocomplete] ⚠️ Could not retrieve ZIP via reverse geocoding:', error);
            }

            // Continue with place selection (with or without ZIP)
            completePlaceSelection(parsedAddress, streetAddress, prediction);
          });

          return; // Exit early, wait for reverse geocoding callback
        }

        // Continue with place selection (ZIP already present or no geometry available)
        completePlaceSelection(parsedAddress, streetAddress, prediction);
      });
    },
    [getPlaceDetails, getZipCodeFromLocation, onChange, onPlaceSelected, onAddressAutofilled, clearSuggestions, apartmentFieldId, trackAnalytics, name]
  );

  /**
   * Complete place selection after address is fully parsed (including ZIP)
   */
  const completePlaceSelection = useCallback((parsedAddress, streetAddress, prediction) => {
    if (DEBUG_MODE) {
      logger.debug('[AddressAutocomplete] 📍 Final parsed address:', parsedAddress);
      logger.debug('[AddressAutocomplete] 🏠 Street address:', streetAddress);
    }

    // 🚨 IMPORTANT: Set flag BEFORE updating value to prevent search
    isSelectingRef.current = true;

    // Close dropdown immediately
    setIsOpen(false);
    clearSuggestions();
    setHighlightedIndex(-1);

    // Update input value with street address
    const displayValue = streetAddress || (prediction ? prediction.description : '');

    if (onChange) {
      onChange({ target: { name, value: displayValue } }, displayValue);
    }

    // 🔧 CRITICAL: Update previousInputRef to prevent duplicate search
    // This syncs the ref IMMEDIATELY after updating the input value
    updatePreviousInput(displayValue);

    // Call main callback with parsed data
    if (onPlaceSelected) {
      onPlaceSelected({ parsedAddress, streetAddress });
    }

    // Reset flag after a short delay to allow search again
    setTimeout(() => {
      isSelectingRef.current = false;
    }, 500);

    // Focus apartment field after address selection
    if (apartmentFieldId) {
      setTimeout(() => {
        const apartmentField = document.getElementById(apartmentFieldId);
        if (apartmentField) {
          apartmentField.focus();
        }
      }, 100);
    }

    // Track analytics
    trackAnalytics();

    // Call side effects callback
    if (onAddressAutofilled) {
      onAddressAutofilled({ parsedAddress, streetAddress });
    }

    if (DEBUG_MODE) {
      logger.debug('[AddressAutocomplete] ✅ Address auto-filled successfully');
    }
  }, [onChange, onPlaceSelected, onAddressAutofilled, clearSuggestions, apartmentFieldId, trackAnalytics, name, updatePreviousInput]);

  /**
   * Handle input change
   */
  const handleInputChange = useCallback(
    (e) => {
      const newValue = e.target.value;

      if (onChange) {
        onChange(e, newValue);
      }

      // Open dropdown if there's input
      if (newValue.trim().length >= 3) {
        setIsOpen(true);
        setHighlightedIndex(-1);
      } else {
        setIsOpen(false);
      }
    },
    [onChange]
  );

  /**
   * Handle keyboard navigation
   */
  const handleKeyDown = useCallback(
    (e) => {
      if (!isOpen || suggestions.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev));
          break;

        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
          break;

        case 'Enter':
          e.preventDefault();
          if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
            handlePlaceSelect(suggestions[highlightedIndex]);
          }
          break;

        case 'Escape':
          e.preventDefault();
          setIsOpen(false);
          setHighlightedIndex(-1);
          break;

        default:
          break;
      }
    },
    [isOpen, suggestions, highlightedIndex, handlePlaceSelect]
  );

  /**
   * Handle input focus
   * NOTE: We don't auto-open dropdown on focus to prevent it from reopening
   * after address selection when shipping methods load. Dropdown only opens
   * when user actively types (via handleInputChange).
   */
  const handleFocus = useCallback(() => {
    // Call external onFocus handler if provided (e.g., to enable Google Places after cart recovery)
    if (onFocus) {
      onFocus();
    }
    // Do nothing else - dropdown opens only when user types
  }, [onFocus]);

  /**
   * Handle input blur (with delay to allow click on suggestion)
   */
  const handleBlur = useCallback(() => {
    setTimeout(() => {
      setIsOpen(false);
      setHighlightedIndex(-1);
    }, 200);
  }, []);

  /**
   * Click outside to close dropdown
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  /**
   * Show dropdown when suggestions arrive
   *
   * ⚠️ DISABLED: This was causing dropdown to reopen after shipping methods loaded.
   * The dropdown is already opened in handleInputChange() when user actively types.
   * We don't want it to auto-open just because suggestions happen to be present.
   */
  // useEffect(() => {
  //   if (suggestions.length > 0 && value.trim().length >= 3) {
  //     setIsOpen(true);
  //   }
  // }, [suggestions, value]);

  /**
   * Highlight matched text in suggestion
   */
  const highlightMatch = useCallback(
    (text) => {
      if (!value) return text;

      const regex = new RegExp(`(${value})`, 'gi');
      const parts = text.split(regex);

      return parts.map((part, index) =>
        regex.test(part) ? (
          <strong key={index} className="address-autocomplete__match">
            {part}
          </strong>
        ) : (
          <span key={index}>{part}</span>
        )
      );
    },
    [value]
  );

  return (
    <div className="address-autocomplete">
      {/* Input Field */}
      <input
        ref={inputRef}
        type="text"
        id={id}
        name={name}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        required={required}
        autoComplete="off"
        aria-autocomplete="list"
        aria-controls="address-autocomplete-dropdown"
        aria-expanded={isOpen}
        aria-activedescendant={
          highlightedIndex >= 0 ? `address-suggestion-${highlightedIndex}` : undefined
        }
        className="address-autocomplete__input"
      />

      {/* Loading Indicator */}
      {loading && <div className="address-autocomplete__loading">Searching...</div>}

      {/* Error Message */}
      {error && (
        <div className="address-autocomplete__error">
          Failed to load suggestions. Please try again.
        </div>
      )}

      {/* Dropdown with Suggestions */}
      {isOpen && suggestions.length > 0 && (
        <div
          ref={dropdownRef}
          id="address-autocomplete-dropdown"
          role="listbox"
          className="address-autocomplete__dropdown"
        >
          {suggestions.map((suggestion, index) => (
            <div
              key={suggestion.place_id}
              id={`address-suggestion-${index}`}
              role="option"
              aria-selected={index === highlightedIndex}
              className={`address-autocomplete__suggestion ${
                index === highlightedIndex ? 'address-autocomplete__suggestion--highlighted' : ''
              }`}
              onClick={() => handlePlaceSelect(suggestion)}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              {/* Location Icon */}
              <svg
                className="address-autocomplete__icon"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                  clipRule="evenodd"
                />
              </svg>

              {/* Suggestion Text */}
              <div className="address-autocomplete__text">
                {highlightMatch(suggestion.description)}
              </div>
            </div>
          ))}

          {/* Powered by Google */}
          <div className="address-autocomplete__powered">
            <img
              src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 116 15' aria-label='Powered by Google'%3E%3Cpath fill='%23333333' d='M4.025 3.572c1.612 0 2.655 1.283 2.655 3.27 0 1.974-1.05 3.27-2.655 3.27-.902 0-1.63-.393-1.974-1.06h-.09v3.057H.95V3.68h.96v1.054h.094c.404-.726 1.16-1.166 2.02-1.166zm-.24 5.63c1.16 0 1.852-.884 1.852-2.36 0-1.477-.692-2.362-1.846-2.362-1.14 0-1.86.91-1.86 2.362 0 1.447.72 2.36 1.858 2.36zm7.072.91c-1.798 0-2.912-1.243-2.912-3.27 0-2.033 1.114-3.27 2.912-3.27 1.8 0 2.913 1.237 2.913 3.27 0 2.027-1.114 3.27-2.913 3.27m0-.91c1.196 0 1.87-.866 1.87-2.36 0-1.5-.674-2.362-1.87-2.362-1.195 0-1.87.862-1.87 2.362 0 1.494.675 2.36 1.87 2.36m12.206-5.518H22.05l-1.244 5.05h-.094L19.3 3.684h-.966l-1.412 5.05h-.094l-1.242-5.05h-1.02L16.336 10h1.02l1.406-4.887h.093L20.268 10h1.025zm3.632.78c-1.008 0-1.71.737-1.787 1.856h3.48c-.023-1.12-.69-1.857-1.693-1.857zm1.664 3.9h1.004c-.305 1.085-1.277 1.747-2.66 1.747-1.752 0-2.848-1.26-2.848-3.26 0-1.986 1.113-3.275 2.847-3.275 1.705 0 2.742 1.213 2.742 3.176v.387h-4.542v.047c.053 1.248.75 2.04 1.822 2.04.815 0 1.366-.3 1.63-.857zM31.03 10h1.01V6.086c0-.89.696-1.535 1.657-1.535.2 0 .563.04.645.06V3.6c-.13-.018-.34-.03-.504-.03-.838 0-1.565.434-1.752 1.05h-.094v-.938h-.96V10zm6.915-5.537c-1.008 0-1.71.738-1.787 1.857h3.48c-.023-1.12-.69-1.857-1.693-1.857m1.664 3.902h1.004c-.304 1.084-1.277 1.746-2.66 1.746-1.752 0-2.848-1.26-2.848-3.26 0-1.986 1.113-3.275 2.847-3.275 1.705 0 2.742 1.213 2.742 3.176v.387h-4.542v.047c.053 1.248.75 2.04 1.822 2.04.815 0 1.366-.3 1.63-.857zm5.01 1.746c-1.62 0-2.658-1.28-2.658-3.265 0-1.98 1.05-3.27 2.654-3.27.878 0 1.622.416 1.98 1.108h.087V1.177h1.008V10h-.96V8.992h-.093c-.4.703-1.15 1.12-2.02 1.12zm.23-5.63c-1.15 0-1.845.89-1.845 2.365s.69 2.36 1.846 2.36c1.15 0 1.858-.9 1.858-2.36 0-1.447-.715-2.362-1.857-2.362zm7.876-3.114h1.024V4.49q.346-.45.827-.69.48-.239 1.043-.237 1.172.001 1.9.806c.49.536.73 1.33.73 2.375q0 1.489-.72 2.473t-1.997.984q-.715 0-1.207-.344-.293-.206-.627-.657v.8h-.97V1.364zm4.02 7.225q.426-.679.426-1.793 0-.99-.425-1.64t-1.25-.65q-.719 0-1.264.532-.541.532-.542 1.757.001.885.224 1.435.416 1.04 1.553 1.04.856 0 1.28-.68zM63.4 3.727h1.167q-.222.603-.99 2.754a145 145 0 0 1-.96 2.635q-.915 2.402-1.29 2.93-.375.526-1.29.527-.221 0-.342-.017a3 3 0 0 1-.296-.065v-.96q.273.074.4.093.119.018.214.018.292 0 .43-.093a.9.9 0 0 0 .232-.237q.03-.05.21-.48.183-.436.264-.646l-2.32-6.457h1.196l1.68 5.11 1.694-5.11zm10.594 1.556V6.87h3.814c-.117.89-.416 1.54-.87 1.998-.557.555-1.427 1.16-2.944 1.16-2.35 0-4.184-1.882-4.184-4.217 0-2.33 1.835-4.214 4.184-4.214a4.07 4.07 0 0 1 2.873 1.135l1.122-1.116C77.04.697 75.77 0 73.99 0c-3.218 0-5.923 2.606-5.923 5.805 0 3.2 2.705 5.804 5.923 5.804 1.738 0 3.048-.57 4.073-1.63 1.05-1.044 1.382-2.52 1.382-3.71 0-.365-.028-.707-.087-.99h-5.37zm10.222-1.29c-2.082 0-3.78 1.574-3.78 3.748 0 2.154 1.698 3.747 3.78 3.747S87.998 9.9 87.998 7.74c0-2.174-1.7-3.748-3.782-3.748zm0 6.018c-1.14 0-2.127-.935-2.127-2.27 0-1.348.982-2.27 2.123-2.27s2.128.922 2.128 2.27c0 1.335-.985 2.27-2.127 2.27zm18.54-5.18h-.06c-.37-.438-1.083-.838-1.985-.838-1.88 0-3.52 1.632-3.52 3.748 0 2.102 1.64 3.747 3.52 3.747.906 0 1.62-.4 1.99-.852h.06v.523c0 1.432-.774 2.2-2.013 2.2-1.012 0-1.64-.723-1.9-1.336l-1.44.593c.414.994 1.51 2.213 3.34 2.213 1.94 0 3.58-1.135 3.58-3.902v-6.74h-1.57v.645zm-1.9 5.18c-1.144 0-2.013-.968-2.013-2.27 0-1.323.87-2.27 2.01-2.27 1.13 0 2.012.967 2.012 2.282.006 1.31-.882 2.258-2.01 2.258zM92.65 3.992c-2.084 0-3.783 1.574-3.783 3.748 0 2.154 1.7 3.747 3.782 3.747 2.08 0 3.78-1.587 3.78-3.747 0-2.174-1.7-3.748-3.78-3.748zm0 6.018c-1.143 0-2.13-.935-2.13-2.27 0-1.348.987-2.27 2.13-2.27 1.14 0 2.126.922 2.126 2.27 0 1.335-.986 2.27-2.127 2.27zM105.622.155h1.628v11.332h-1.628m6.655-1.477c-.843 0-1.44-.38-1.83-1.135l5.04-2.07-.168-.426c-.315-.84-1.275-2.39-3.228-2.39-1.94 0-3.554 1.515-3.554 3.75 0 2.1 1.595 3.744 3.736 3.744 1.725 0 2.724-1.05 3.14-1.658l-1.285-.852c-.428.62-1.01 1.032-1.855 1.032zm-.117-4.612c.668 0 1.24.342 1.427.826l-3.405 1.4c0-1.574 1.122-2.226 1.978-2.226'%3E%3C/path%3E%3C/svg%3E"
              alt="Powered by Google"
              className="address-autocomplete__powered-img"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
